import React, { useEffect, useState } from 'react';
import styles from './Evaluations.module.scss';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Search from '../../../components/search/Search';
import Filter from '../../../components/filter/Filter';
import Card from '../../../components/card/Card';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import axiosClient from '../../../api/axiosClient';
import LoadMore from '../../../components/loadMore/loadMore';
import { useToasts } from '../../../contexts/ToastContext';
import { format } from 'date-fns';
import TransferEvaluationModal from './TransferEvaluationModal';

const Evaluations = () => {  
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('Today');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [loadingCard, setLoadingCard] = useState(null);
  const [transferEvaluationId, setTransferEvaluationId] = useState(null);
  const navigate = useNavigate();
  const { showToast } = useToasts();

  useEffect(() => {
    // Reset the page when the searchKeyword or selectedFilter changes
    setPage(1);
    setData([]); // Clear previous data
  }, [searchKeyword, selectedFilter]); // Only reset page on filter or search change

  useEffect(() => {
    const params = {
      page: page,
      limit: 20
    };
    if(searchKeyword) {
      params.search = searchKeyword;
    }
    if(selectedFilter) {
      params.filter = selectedFilter;
    }
    const fetchData = async () => {
      if(page === 1) {
        setIsLoading(true);
      }
      await axiosClient.get(`evaluation-requests`, {
        params
      })
      .then ((response) => {
        if(response.data?.evaluationRequests) {
          if(page === 1) {
            setData([]);
          }
          setData((prevData) => {
            return [...prevData, ...response.data.evaluationRequests.map((item) => ({
              id: item.id,
              studentId: item.student?.id ?? item.id,
              profilePicture: item.student?.profilePicture ?? null,
              firstName: item.student?.firstName ?? '',
              lastName: item.student?.lastName ?? '',
              data: [
                {
                  label: 'Rotation Type',
                  value: item?.student?.specialities.length > 0 ? 
                    `${item?.student?.specialities[0].name} ${item?.student?.specialities.length > 1 
                      ? `+${item?.student?.specialities.length - 1}` : ''}` : 
                    '',
                  tooltip: item?.student?.specialities.length > 1 ? 
                    item?.student?.specialities.map(item => item.name).join(', ') :
                    ''
                },
                {
                  label: 'Requested Date',
                  value: item.date ? format(item.date, 'MM/dd/yyyy') : ''
                },
                {
                  label: 'Year',
                  value: item.student?.year?.year ?? ''
                },
                {
                  label: 'Semester',
                  value: item.student?.semester?.semester ?? ''
                },
                {
                  label: 'Month',
                  value: item.student?.month?.month ?? ''
                },
              ],
              isAssigned: item.student?.isAllocated ?? null,
              status: item.status ?? null,
            }))];
          });
          setTotalPages(response.data.totalPages);
        }
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
        setLoadMoreLoading(false);
      });
    };

    fetchData();
  }, [searchKeyword, selectedFilter, page, showToast]);

  const loadMore = () => {
    setLoadMoreLoading(true);
    setPage((prevPage) => prevPage + 1);
  };

  const handleUpdateStatus = async (id, status) => {
    setLoadingCard(id);
    await axiosClient.patch(`evaluation-requests/status/${id}`, {
      status,
      comment: status
    })
    .then(() => {
      setData((prevData) => {
        const updatedData = prevData.map(item =>
          item.id === id ? { ...item, status: status } : item
        );
        return updatedData;
      });
      showToast(`You have ${status.toLowerCase()} the evaluation request`, status === 'Accepted' ? 'info' : 'error');
    })
    .catch((error) => {
      showToast(error.message, error);
    })
    .finally(() => {
      setLoadingCard(null);
    });
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle
          label="Your Evaluations"
        />
        <div className={styles.search}>
          <Search
            placeholder="Search by Student Name"
            onSubmit={(keyword) => {
              setSearchKeyword(keyword);
            }}
          />
        </div>
        <div className={styles.filter}>
          <Filter
            filters={[
              {
                label: 'Today',
                value: 'Today'
              },
              {
                label: 'This Week',
                value: 'ThisWeek'
              },
              {
                label: 'This Month',
                value: 'ThisMonth'
              },
              {
                label: 'All',
                value: ''
              },
              {
                label: 'Pending',
                value: 'Pending'
              },
              {
                label: 'Accepted',
                value: 'Accepted'
              },
              {
                label: 'Declined',
                value: 'Declined'
              }
            ]}
            selected={selectedFilter}
            onChange={(filter) => {
              setSelectedFilter(filter);
            }}
          />
        </div>
      </div>
      <div className={styles.container}>
        {data.length > 0 ? <div className={styles.cardList}>
          {data.map((item, index) => {
            return (
              <div className={styles.cardItem} key={`${item.name}-${index}`}>
                <Card
                  {...item}
                  onClick={() => {
                    navigate(`/student-overview/${item.studentId}`);
                  }}
                  onActionClick={(status) => {
                    handleUpdateStatus(item.id, status);
                  }}
                  onTransferClick={() => setTransferEvaluationId(item.id)}
                  isLoading={loadingCard === item.id}
                />
              </div>
            );
          })}
          {(loadMoreLoading || page < totalPages) && <LoadMore isLoading={loadMoreLoading} onClick={loadMore} />}
        </div> : !isLoading && <div className={styles.emptyList}>
          <p>No evaluations available</p>
        </div>}
      </div>
      {transferEvaluationId && (
        <TransferEvaluationModal
          evaluationId={transferEvaluationId}
          onClose={() => setTransferEvaluationId(null)}
          onSuccess={() => {
            setPage(1);
            setData([]);
          }}
        />
      )}
    </>
  );
}

export default Evaluations;
